import { PLAN_PHASE, PLAN_PHASE_LABELS, formatSets, type TrainingExerciseDto, type TrainingPlanDto, type TrainingPlanItemDto, type TrainingVideoDto } from '@ukinfo/shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { itemExerciseId } from '../../lib/training';
import { Badge } from '../ui';
import { CategoryBadge, ExerciseText, ExternalLink, SketchWithLegend } from './common';
import { ExerciseVideoList } from './ExerciseVideos';

/**
 * Anzeige einer Einheit: Phasen mit Uebungen, Skizze und Beschreibung klappen auf.
 *
 * Mit `offline` (gespeicherte Einheit) kommen die Beschreibungen aus dem Geraet statt vom Server; die
 * Ansicht ist dieselbe, nur lesend: ohne Link zur Uebung, Videos ueber `videos` (aus dem Geraetespeicher).
 */
export interface OfflinePlanSource {
  exercises: Record<string, TrainingExerciseDto>;
  videos: (videos: TrainingVideoDto[]) => ReactNode;
}

function ItemDetails({ item, offline }: { item: TrainingPlanItemDto; offline?: OfflinePlanSource }): JSX.Element {
  const id = itemExerciseId(item);
  const query = useQuery({
    queryKey: ['training-exercise', id],
    queryFn: () => api.get<TrainingExerciseDto>(`/api/training/exercises/${id}`),
    enabled: id !== null && !offline,
    staleTime: 60_000,
  });
  const data = offline ? (id ? offline.exercises[id] : undefined) : query.data;
  const isLoading = !offline && query.isLoading;
  const exercise = item.exercise;
  return (
    <div className="mt-3 space-y-3 border-t border-slate-100 pt-3">
      {exercise?.isLink && exercise.linkUrl && (
        <p className="text-sm text-slate-700">
          Diese Übung liegt auf einer anderen Seite: <ExternalLink href={exercise.linkUrl}>{exercise.linkUrl}</ExternalLink>
        </p>
      )}
      {exercise?.diagram && <SketchWithLegend exercise={{ diagram: exercise.diagram, title: item.title }} />}
      {isLoading && <p className="text-sm text-slate-500">Beschreibung wird geladen …</p>}
      {data && !data.isLink && <ExerciseText exercise={data} />}
      {data && (offline ? offline.videos(data.videos) : <ExerciseVideoList videos={data.videos} />)}
      {id !== null && !offline && (
        <Link to={`/sport/uebungen/${id}`} className="inline-block text-sm font-medium text-brand-700">
          Übung öffnen
        </Link>
      )}
    </div>
  );
}

function ItemRow({ item, offline }: { item: TrainingPlanItemDto; offline?: OfflinePlanSource }): JSX.Element {
  const [open, setOpen] = useState(false);
  const exercise = item.exercise;
  // Eine Katalogübung kann aus dem Katalog entfallen sein - der Eintrag bleibt lesbar.
  const missing = item.exerciseId === null && item.catalogKey !== null && exercise === null;
  const expandable = exercise !== null;
  // Durchgaenge der Uebung ("4 × 3 Min., 1 Min. Pause"): die Minuten daneben sind die geplante Zeit.
  const sets = exercise ? formatSets(exercise) : null;
  return (
    <li className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="break-words font-medium text-slate-900">{item.title}</p>
          {sets && <p data-testid="durchgaenge" className="text-sm text-slate-600">{sets}</p>}
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {exercise && <CategoryBadge category={exercise.category} />}
            {exercise?.source === 'CATALOG' && <Badge tone="brand">Standard</Badge>}
            {exercise?.isLink && <Badge tone="info">Link</Badge>}
            {exercise?.archived && <Badge tone="danger">Archiviert</Badge>}
            {missing && <Badge tone="warning">Nicht mehr im Katalog</Badge>}
          </div>
        </div>
        <span className="shrink-0 text-sm font-medium text-slate-700">{item.durationMinutes} Min.</span>
      </div>
      {item.note && <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-slate-600">{item.note}</p>}
      {expandable && (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          className="mt-2 min-h-11 text-sm font-medium text-brand-700 sm:min-h-0"
        >
          {open ? 'Skizze und Beschreibung ausblenden' : exercise.diagram ? 'Skizze und Beschreibung zeigen' : 'Beschreibung zeigen'}
          <span className="sr-only"> · {item.title}</span>
        </button>
      )}
      {open && <ItemDetails item={item} offline={offline} />}
    </li>
  );
}

export function PlanView({ plan, offline }: { plan: TrainingPlanDto; offline?: OfflinePlanSource }): JSX.Element {
  return (
    <div className="space-y-4">
      {(plan.focus || plan.notes) && (
        <div className="space-y-1 text-sm text-slate-700">
          {plan.focus && <p><span className="font-medium">Schwerpunkt:</span> {plan.focus}</p>}
          {plan.notes && <p className="whitespace-pre-wrap break-words">{plan.notes}</p>}
        </div>
      )}
      {PLAN_PHASE.map((phase) => {
        const items = plan.items.filter((item) => item.phase === phase).sort((a, b) => a.position - b.position);
        return (
          <section key={phase} aria-label={PLAN_PHASE_LABELS[phase]} className="space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <h4 className="text-base font-semibold text-slate-900">{PLAN_PHASE_LABELS[phase]}</h4>
              <span className="text-sm text-slate-500">{plan.minutesByPhase[phase]} Min.</span>
            </div>
            {items.length === 0 ? (
              <p className="text-sm text-slate-500">Keine Einträge</p>
            ) : (
              <ul className="space-y-2">
                {items.map((item) => (
                  <ItemRow key={item.id} item={item} offline={offline} />
                ))}
              </ul>
            )}
          </section>
        );
      })}
      <p className="text-sm font-medium text-slate-900">Gesamt: {plan.totalMinutes} Min.</p>
    </div>
  );
}
